<template>
  <main class="mx-auto w-full max-w-[1320px] pb-14">
    <header class="flex flex-col gap-5 border-b border-zinc-200 pb-7 sm:flex-row sm:items-center sm:justify-between">
      <div class="flex items-center gap-4">
        <button
          type="button"
          class="flex h-10 w-10 items-center justify-center rounded-xl border border-zinc-300 bg-white text-zinc-700 shadow-sm hover:bg-zinc-50"
          aria-label="Back to webinars"
          @click="$emit('close')"
        >
          <ArrowLeftIcon class="h-5 w-5" />
        </button>
        <div>
          <p class="text-xs font-bold uppercase tracking-[0.14em] text-blue-600">Event builder</p>
          <h1 class="mt-1 text-2xl font-bold tracking-tight text-zinc-950">
            {{ isEditing ? 'Edit webinar event' : 'Create webinar event' }}
          </h1>
        </div>
      </div>
      <div
        class="rounded-full px-3 py-1.5 text-xs font-semibold"
        :class="prototypeMode ? 'border border-amber-200 bg-amber-50 text-amber-800' : 'border border-emerald-200 bg-emerald-50 text-emerald-800'"
      >
        {{ prototypeMode ? 'Frontend preview · Nothing will be saved' : 'Connected to Webinar events' }}
      </div>
    </header>

    <div class="mt-8 grid gap-8 lg:grid-cols-[240px_minmax(0,1fr)]">
      <aside>
        <nav class="space-y-2 lg:sticky lg:top-24">
          <button
            v-for="(step, index) in steps"
            :key="step.id"
            type="button"
            class="flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition"
            :class="currentStep === index ? 'bg-blue-50' : index < currentStep ? 'hover:bg-zinc-50' : 'opacity-60'"
            @click="goToStep(index)"
          >
            <span
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-bold"
              :class="index < currentStep
                ? 'border-emerald-500 bg-emerald-500 text-white'
                : currentStep === index
                  ? 'border-blue-600 bg-blue-600 text-white'
                  : 'border-zinc-300 bg-white text-zinc-500'"
            >
              <CheckIcon v-if="index < currentStep" class="h-4 w-4" />
              <span v-else>{{ index + 1 }}</span>
            </span>
            <span>
              <span class="block text-sm font-semibold" :class="currentStep === index ? 'text-blue-900' : 'text-zinc-800'">{{ step.label }}</span>
              <span class="mt-0.5 block text-xs leading-5 text-zinc-500">{{ step.caption }}</span>
            </span>
          </button>
        </nav>
      </aside>

      <section class="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
        <div class="border-b border-zinc-100 px-6 py-5 sm:px-8">
          <p class="text-xs font-bold uppercase tracking-[0.12em] text-blue-600">Step {{ currentStep + 1 }} of {{ steps.length }}</p>
          <h2 class="mt-1 text-xl font-bold text-zinc-950">{{ steps[currentStep].heading }}</h2>
          <p class="mt-1 text-sm leading-6 text-zinc-500">{{ steps[currentStep].description }}</p>
        </div>

        <form class="px-6 py-7 sm:px-8" @submit.prevent="handlePrimaryAction">
          <div v-if="currentStep === 0" class="space-y-6">
            <div>
              <label for="webinar-product" class="block text-sm font-semibold text-zinc-800">Webinar product</label>
              <p class="mt-1 text-xs text-zinc-500">Choose where members will find this event.</p>
              <div class="relative mt-2">
                <select
                  id="webinar-product"
                  v-model="form.productId"
                  class="block w-full appearance-none rounded-xl border border-zinc-300 bg-white py-3 pl-4 pr-10 text-sm text-zinc-900 focus:border-blue-500 focus:ring-blue-500"
                >
                  <option v-for="product in products" :key="product.id" :value="String(product.id)">{{ product.name }}</option>
                </select>
                <ChevronDownIcon class="pointer-events-none absolute right-3 top-3.5 h-5 w-5 text-zinc-400" />
              </div>
            </div>

            <div>
              <label for="event-title" class="block text-sm font-semibold text-zinc-800">Event title</label>
              <input
                id="event-title"
                v-model.trim="form.title"
                type="text"
                maxlength="120"
                placeholder="Give your webinar a clear, useful title"
                class="mt-2 block w-full rounded-xl border-zinc-300 px-4 py-3 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              <div class="mt-1.5 flex justify-between text-xs text-zinc-400">
                <span v-if="errors.title" class="font-medium text-red-600">{{ errors.title }}</span>
                <span v-else>Members will see this on registration and waiting pages.</span>
                <span>{{ form.title.length }}/120</span>
              </div>
            </div>

            <div>
              <label for="event-description" class="block text-sm font-semibold text-zinc-800">Description</label>
              <textarea
                id="event-description"
                v-model.trim="form.description"
                rows="5"
                maxlength="600"
                placeholder="What will members learn or experience?"
                class="mt-2 block w-full resize-none rounded-xl border-zinc-300 px-4 py-3 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
              ></textarea>
              <p class="mt-1.5 text-right text-xs text-zinc-400">{{ form.description.length }}/600</p>
            </div>

            <div class="rounded-2xl border border-zinc-200 p-5">
              <div>
                <p class="text-sm font-semibold text-zinc-900">Registration-page bullets</p>
                <p class="mt-1 text-xs leading-5 text-zinc-500">Add up to four concise takeaways beneath the event description.</p>
              </div>
              <div class="mt-4 space-y-3">
                <div v-for="(_bullet, index) in form.bulletPoints" :key="`webinar-bullet-${index}`" class="flex items-center gap-3">
                  <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-700">{{ index + 1 }}</span>
                  <input
                    v-model.trim="form.bulletPoints[index]"
                    type="text"
                    maxlength="300"
                    :placeholder="`Bullet point ${index + 1}`"
                    class="block w-full rounded-xl border-zinc-300 px-4 py-2.5 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div class="rounded-2xl border border-zinc-200 bg-zinc-50 p-5">
              <div class="flex flex-col gap-5 sm:flex-row sm:items-start">
                <button
                  type="button"
                  class="group relative flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-zinc-300 bg-white hover:border-blue-500"
                  @click="openMediaPicker('hostAvatar')"
                >
                  <img v-if="hostAvatarPreview" :src="hostAvatarPreview" alt="Host avatar" class="h-full w-full object-cover" />
                  <UserIcon v-else class="h-8 w-8 text-zinc-400" />
                  <span class="absolute inset-0 hidden items-center justify-center bg-black/55 text-[11px] font-bold text-white group-hover:flex">Change</span>
                </button>
                <div class="min-w-0 flex-1">
                  <label for="host-name" class="block text-sm font-semibold text-zinc-800">Host name</label>
                  <input
                    id="host-name"
                    v-model.trim="form.hostName"
                    type="text"
                    maxlength="160"
                    placeholder="Host display name"
                    class="mt-2 block w-full rounded-xl border-zinc-300 px-4 py-3 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                  <p v-if="errors.hostName" class="mt-1.5 text-xs font-medium text-red-600">{{ errors.hostName }}</p>
                  <div class="mt-3 flex flex-wrap gap-3">
                    <button type="button" class="text-xs font-semibold text-blue-600 hover:text-blue-700" @click="openMediaPicker('hostAvatar')">
                      {{ form.hostAvatarMediaFileId ? 'Change host photo' : 'Upload or choose host photo' }}
                    </button>
                    <button v-if="form.hostAvatarMediaFileId" type="button" class="text-xs font-semibold text-zinc-500 hover:text-red-600" @click="clearHostAvatar">Remove</button>
                  </div>
                  <p class="mt-2 text-xs text-zinc-500">Reuse a creator photo from Media Content or upload a new image.</p>
                </div>
              </div>
            </div>

            <div class="rounded-2xl border border-zinc-200 p-5">
              <div class="flex flex-col gap-5 sm:flex-row">
                <div class="relative flex aspect-video w-full max-w-[260px] shrink-0 items-center justify-center overflow-hidden rounded-xl bg-zinc-950">
                  <img v-if="introVideoThumbnailPreview" :src="introVideoThumbnailPreview" alt="Intro video thumbnail" class="h-full w-full object-cover" />
                  <FilmIcon v-else class="h-10 w-10 text-zinc-600" />
                  <span v-if="form.introVideoMediaFileId" class="absolute bottom-2 left-2 rounded-full bg-black/75 px-2 py-1 text-[10px] font-semibold text-white">Intro video</span>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-bold text-zinc-900">Webinar intro video</p>
                  <p class="mt-1 text-xs leading-5 text-zinc-500">Optional video shown before the Webinar. New uploads use Platforma’s existing compression, thumbnail generation, and HLS processing.</p>
                  <p v-if="introVideoFileName" class="mt-3 truncate text-xs font-semibold text-zinc-700">{{ introVideoFileName }}</p>
                  <div class="mt-4 flex flex-wrap gap-3">
                    <button type="button" class="rounded-lg bg-zinc-950 px-3.5 py-2 text-xs font-semibold text-white hover:bg-zinc-800" @click="openMediaPicker('introVideo')">
                      {{ form.introVideoMediaFileId ? 'Change video' : 'Upload or choose video' }}
                    </button>
                    <button v-if="form.introVideoMediaFileId" type="button" class="rounded-lg border border-zinc-300 px-3.5 py-2 text-xs font-semibold text-zinc-600 hover:bg-zinc-50" @click="clearIntroVideo">Remove</button>
                  </div>
                </div>
              </div>

              <div class="mt-5 border-t border-zinc-100 pt-5">
                <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p class="text-sm font-semibold text-zinc-900">Intro video thumbnail</p>
                    <p class="mt-1 text-xs text-zinc-500">Upload or choose the image members see before playing the intro.</p>
                  </div>
                  <div class="flex flex-wrap gap-3">
                    <button type="button" class="rounded-lg border border-zinc-300 px-3.5 py-2 text-xs font-semibold text-zinc-700 hover:border-blue-400 hover:bg-blue-50" @click="openMediaPicker('introThumbnail')">
                      <PhotoIcon class="mr-1.5 inline h-4 w-4" />
                      {{ form.coverMediaFileId ? 'Change thumbnail' : 'Choose thumbnail' }}
                    </button>
                    <button v-if="form.coverMediaFileId" type="button" class="text-xs font-semibold text-zinc-500 hover:text-red-600" @click="clearIntroThumbnail">Remove</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="currentStep === 1" class="space-y-7">
            <div class="grid gap-4 sm:grid-cols-2">
              <button
                type="button"
                class="rounded-2xl border-2 p-5 text-left transition"
                :class="form.startMode === 'scheduled' ? 'border-blue-600 bg-blue-50' : 'border-zinc-200 hover:border-zinc-300'"
                @click="form.startMode = 'scheduled'"
              >
                <CalendarDaysIcon class="h-6 w-6 text-blue-600" />
                <p class="mt-4 text-sm font-bold text-zinc-950">Schedule for later</p>
                <p class="mt-1 text-xs leading-5 text-zinc-500">Choose a future date and prepare the event in advance.</p>
              </button>
              <button
                type="button"
                class="rounded-2xl border-2 p-5 text-left transition"
                :class="form.startMode === 'go_live_now' ? 'border-red-500 bg-red-50' : 'border-zinc-200 hover:border-zinc-300'"
                @click="form.startMode = 'go_live_now'"
              >
                <SignalIcon class="h-6 w-6 text-red-500" />
                <p class="mt-4 text-sm font-bold text-zinc-950">Go live now</p>
                <p class="mt-1 text-xs leading-5 text-zinc-500">Save the event and move directly into the Studio setup.</p>
              </button>
            </div>

            <div v-if="form.startMode === 'scheduled'">
              <label class="block text-sm font-semibold text-zinc-800">Event date and start time</label>
              <div class="mt-2">
                <WebinarEventScheduler
                  v-model:date="form.date"
                  v-model:time="form.time"
                  :timezone="form.timezone"
                />
              </div>
              <div class="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                <p v-if="errors.date" class="text-xs font-medium text-red-600">{{ errors.date }}</p>
                <p v-if="errors.time" class="text-xs font-medium text-red-600">{{ errors.time }}</p>
              </div>
            </div>

            <div v-if="form.startMode === 'scheduled'">
              <label for="event-timezone" class="block text-sm font-semibold text-zinc-800">Timezone</label>
              <div class="relative mt-2">
                <select id="event-timezone" v-model="form.timezone" class="block w-full appearance-none rounded-xl border-zinc-300 bg-white py-3 pl-4 pr-10 text-sm focus:border-blue-500 focus:ring-blue-500">
                  <option value="America/New_York">Eastern Time — New York</option>
                  <option value="America/Chicago">Central Time — Chicago</option>
                  <option value="America/Denver">Mountain Time — Denver</option>
                  <option value="America/Los_Angeles">Pacific Time — Los Angeles</option>
                  <option value="UTC">UTC</option>
                </select>
                <ChevronDownIcon class="pointer-events-none absolute right-3 top-3.5 h-5 w-5 text-zinc-400" />
              </div>
            </div>

            <div class="grid gap-4 sm:grid-cols-2">
              <label class="flex cursor-pointer gap-3 rounded-xl border p-4 transition" :class="form.registrationRequired ? 'border-blue-500 bg-blue-50/60' : 'border-zinc-200 hover:border-zinc-300'">
                <input v-model="form.registrationRequired" type="checkbox" class="mt-1 h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500" />
                <span>
                  <span class="block text-sm font-semibold text-zinc-900">Require registration</span>
                  <span class="mt-1 block text-xs leading-5 text-zinc-500">Members register before entering the webinar.</span>
                </span>
              </label>
              <label class="flex cursor-pointer gap-3 rounded-xl border p-4 transition" :class="form.sendReminders ? 'border-blue-500 bg-blue-50/60' : 'border-zinc-200 hover:border-zinc-300'">
                <input v-model="form.sendReminders" type="checkbox" class="mt-1 h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500" />
                <span>
                  <span class="block text-sm font-semibold text-zinc-900">Event reminders</span>
                  <span class="mt-1 block text-xs leading-5 text-zinc-500">Prepare notification reminders for registered members.</span>
                </span>
              </label>
            </div>

            <div v-if="form.startMode === 'scheduled'" class="rounded-xl border border-zinc-200 bg-zinc-50 p-5">
              <div class="flex items-center justify-between gap-4">
                <div>
                  <p class="text-sm font-semibold text-zinc-900">Waiting room opens</p>
                  <p class="mt-1 text-xs text-zinc-500">Members can enter before the broadcast starts.</p>
                </div>
                <select v-model="form.waitingRoomMinutes" class="w-36 rounded-lg border-zinc-300 bg-white py-2 text-sm focus:border-blue-500 focus:ring-blue-500">
                  <option :value="5">5 minutes before</option>
                  <option :value="10">10 minutes before</option>
                  <option :value="15">15 minutes before</option>
                  <option :value="30">30 minutes before</option>
                </select>
              </div>
            </div>
          </div>

          <div v-else-if="currentStep === 2" class="space-y-7">
            <div class="grid gap-4 md:grid-cols-2">
              <button
                type="button"
                class="rounded-2xl border-2 p-5 text-left transition"
                :class="form.source === 'browser' ? 'border-blue-600 bg-blue-50 shadow-sm' : 'border-zinc-200 hover:border-zinc-300'"
                @click="form.source = 'browser'"
              >
                <div class="flex items-start justify-between gap-3">
                  <span class="flex h-11 w-11 items-center justify-center rounded-xl bg-white shadow-sm"><VideoCameraIcon class="h-6 w-6 text-blue-600" /></span>
                  <CheckCircleIcon v-if="form.source === 'browser'" class="h-6 w-6 text-blue-600" />
                </div>
                <p class="mt-5 text-base font-bold text-zinc-950">Web browser studio</p>
                <p class="mt-2 text-sm leading-6 text-zinc-500">Use your webcam, microphone, and screen without leaving Platforma.</p>
                <span class="mt-4 inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">Fastest setup</span>
              </button>

              <button
                type="button"
                class="rounded-2xl border-2 p-5 text-left transition"
                :class="form.source === 'obs' ? 'border-blue-600 bg-blue-50 shadow-sm' : 'border-zinc-200 hover:border-zinc-300'"
                @click="form.source = 'obs'"
              >
                <div class="flex items-start justify-between gap-3">
                  <span class="flex h-11 w-11 items-center justify-center rounded-xl bg-white shadow-sm"><ComputerDesktopIcon class="h-6 w-6 text-violet-600" /></span>
                  <CheckCircleIcon v-if="form.source === 'obs'" class="h-6 w-6 text-blue-600" />
                </div>
                <p class="mt-5 text-base font-bold text-zinc-950">OBS / streaming software</p>
                <p class="mt-2 text-sm leading-6 text-zinc-500">Use scenes, overlays, multiple cameras, and a professional encoder.</p>
                <span class="mt-4 inline-flex rounded-full bg-violet-100 px-2.5 py-1 text-xs font-semibold text-violet-700">Advanced control</span>
              </button>
            </div>

            <div v-if="form.source === 'obs'" class="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div class="flex gap-3">
                <KeyIcon class="h-5 w-5 shrink-0 text-amber-700" />
                <div>
                  <p class="text-sm font-semibold text-amber-900">Stream credentials will appear in the Studio</p>
                  <p class="mt-1 text-xs leading-5 text-amber-700">Server URL, stream key, connection testing, and key rotation will be connected after the streaming infrastructure is ready.</p>
                </div>
              </div>
            </div>

            <div>
              <p class="text-sm font-semibold text-zinc-800">Member experience</p>
              <div class="mt-3 divide-y divide-zinc-100 rounded-xl border border-zinc-200">
                <label class="flex cursor-pointer items-center justify-between gap-4 p-4">
                  <span class="flex items-center gap-3">
                    <ChatBubbleLeftRightIcon class="h-5 w-5 text-zinc-500" />
                    <span><span class="block text-sm font-semibold text-zinc-900">Live chat</span><span class="mt-0.5 block text-xs text-zinc-500">Let members react and participate.</span></span>
                  </span>
                  <input v-model="form.enableChat" type="checkbox" class="h-5 w-5 rounded border-zinc-300 text-blue-600 focus:ring-blue-500" />
                </label>
                <label class="flex cursor-pointer items-center justify-between gap-4 p-4">
                  <span class="flex items-center gap-3">
                    <FilmIcon class="h-5 w-5 text-zinc-500" />
                    <span><span class="block text-sm font-semibold text-zinc-900">Record for replay</span><span class="mt-0.5 block text-xs text-zinc-500">Prepare a replay when the event ends.</span></span>
                  </span>
                  <input v-model="form.recordReplay" type="checkbox" class="h-5 w-5 rounded border-zinc-300 text-blue-600 focus:ring-blue-500" />
                </label>
                <label class="flex cursor-pointer items-center justify-between gap-4 p-4">
                  <span class="flex items-center gap-3">
                    <HandRaisedIcon class="h-5 w-5 text-zinc-500" />
                    <span><span class="block text-sm font-semibold text-zinc-900">Member reactions</span><span class="mt-0.5 block text-xs text-zinc-500">Show live emoji reactions over the event.</span></span>
                  </span>
                  <input v-model="form.enableReactions" type="checkbox" class="h-5 w-5 rounded border-zinc-300 text-blue-600 focus:ring-blue-500" />
                </label>
                <label class="flex cursor-pointer items-center justify-between gap-4 p-4">
                  <span class="flex items-center gap-3">
                    <ShieldCheckIcon class="h-5 w-5 text-zinc-500" />
                    <span><span class="block text-sm font-semibold text-zinc-900">Moderator control room</span><span class="mt-0.5 block text-xs text-zinc-500">Create a private moderator link when this event is saved.</span></span>
                  </span>
                  <input v-model="form.enableModeratorAccess" type="checkbox" class="h-5 w-5 rounded border-zinc-300 text-blue-600 focus:ring-blue-500" />
                </label>
              </div>
              <label v-if="form.enableModeratorAccess" class="mt-4 block rounded-xl border border-violet-200 bg-violet-50 p-4">
                <span class="text-sm font-semibold text-violet-950">Visible moderator name</span>
                <span class="mt-1 block text-xs leading-5 text-violet-700">The shareable link will be available in the Moderator tab inside the Webinar Studio.</span>
                <input v-model.trim="form.moderatorDisplayName" type="text" maxlength="160" class="mt-3 block w-full rounded-lg border-violet-200 bg-white px-3 py-2.5 text-sm text-zinc-900 focus:border-violet-500 focus:ring-violet-500" placeholder="Moderator" />
                <span class="mt-4 block text-sm font-semibold text-violet-950">Moderator avatar</span>
                <span class="mt-1 block text-xs leading-5 text-violet-700">Upload a new image or reuse one from this creator's Media Content.</span>
                <button type="button" class="mt-3 flex w-full items-center gap-3 rounded-xl border border-violet-200 bg-white p-3 text-left hover:border-violet-400" @click.prevent="openMediaPicker('moderatorAvatar')">
                  <img v-if="moderatorAvatarPreview" :src="moderatorAvatarPreview" alt="Moderator avatar" class="h-12 w-12 rounded-full object-cover" />
                  <span v-else class="flex h-12 w-12 items-center justify-center rounded-full bg-violet-100 text-violet-600"><PhotoIcon class="h-6 w-6" /></span>
                  <span><span class="block text-sm font-semibold text-zinc-900">{{ form.moderatorAvatarMediaFileId ? 'Change moderator avatar' : 'Choose moderator avatar' }}</span><span class="mt-0.5 block text-xs text-zinc-500">JPG, PNG, WebP, GIF, or AVIF</span></span>
                </button>
              </label>
            </div>
          </div>

          <div v-else class="space-y-6">
            <div class="overflow-hidden rounded-2xl border border-zinc-200">
              <div class="relative aspect-[2.4/1] min-h-[190px] overflow-hidden bg-zinc-950">
                <img :src="introVideoThumbnailPreview || webinarImage" alt="Intro video thumbnail" class="h-full w-full object-cover opacity-60" />
                <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent"></div>
                <div class="absolute bottom-5 left-5 right-5">
                  <p class="text-xs font-bold uppercase tracking-[0.12em] text-blue-300">{{ selectedProductName }}</p>
                  <h3 class="mt-1 text-xl font-bold text-white sm:text-2xl">{{ form.title || 'Untitled webinar event' }}</h3>
                </div>
              </div>
              <div class="grid divide-y divide-zinc-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                <div class="p-4"><p class="text-xs font-medium text-zinc-500">Start</p><p class="mt-1 text-sm font-semibold text-zinc-900">{{ reviewDate }}</p></div>
                <div class="p-4"><p class="text-xs font-medium text-zinc-500">Broadcast</p><p class="mt-1 text-sm font-semibold text-zinc-900">{{ form.source === 'obs' ? 'OBS / streaming software' : 'Web browser studio' }}</p></div>
                <div class="p-4"><p class="text-xs font-medium text-zinc-500">Registration</p><p class="mt-1 text-sm font-semibold text-zinc-900">{{ form.registrationRequired ? 'Required' : 'Not required' }}</p></div>
              </div>
            </div>

            <div class="rounded-xl border border-blue-200 bg-blue-50 p-5">
              <div class="flex gap-3">
                <InformationCircleIcon class="h-5 w-5 shrink-0 text-blue-700" />
                <div>
                  <p class="text-sm font-semibold text-blue-900">{{ prototypeMode ? 'This event stays in frontend preview mode.' : 'The event will be saved to this Webinar product.' }}</p>
                  <p class="mt-1 text-xs leading-5 text-blue-700">{{ prototypeMode ? 'The preview event is cleared when this page reloads.' : 'Media remains in the creator’s Media Content library and can be reused for future events.' }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-9 flex flex-col-reverse gap-3 border-t border-zinc-100 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              class="rounded-xl px-5 py-3 text-sm font-semibold text-zinc-600 hover:bg-zinc-100 disabled:invisible"
              :disabled="currentStep === 0"
              @click="currentStep -= 1"
            >
              Back
            </button>
            <div class="flex flex-col-reverse gap-3 sm:flex-row">
              <button
                v-if="currentStep === steps.length - 1"
                type="button"
                class="rounded-xl border border-zinc-300 bg-white px-5 py-3 text-sm font-semibold text-zinc-800 hover:bg-zinc-50"
                @click="saveEvent('Draft')"
              >
                Save as draft
              </button>
              <span class="inline-flex" :title="previewAvailable ? 'Open the member-facing Webinar design in Theme Editor' : 'Preview available after creation'">
                <button
                  type="button"
                  class="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-5 py-3 text-sm font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-100 disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-zinc-200 disabled:bg-zinc-100 disabled:text-zinc-400"
                  :disabled="!previewAvailable"
                  @click="openWebinarPreview"
                >
                  <EyeIcon class="h-4 w-4" />
                  Webinar preview
                </button>
              </span>
              <button
                type="submit"
                class="inline-flex items-center justify-center gap-2 rounded-xl bg-[#2478ff] px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-600"
                :disabled="saving"
              >
                {{ primaryButtonLabel }}
                <ArrowRightIcon class="h-4 w-4" />
              </button>
            </div>
          </div>
          <p v-if="saveError" class="mt-4 text-right text-sm font-medium text-red-600">{{ saveError }}</p>
        </form>
      </section>
    </div>
  </main>

  <WebinarMediaPickerModal
    :show="Boolean(activeMediaPicker)"
    :media-type="mediaPickerType"
    :title="mediaPickerTitle"
    :upload-origin="mediaPickerOrigin"
    :allow-upload="!prototypeMode"
    @close="activeMediaPicker = ''"
    @select="applySelectedMedia"
  />
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  ComputerDesktopIcon,
  EyeIcon,
  FilmIcon,
  HandRaisedIcon,
  InformationCircleIcon,
  KeyIcon,
  PhotoIcon,
  CalendarDaysIcon,
  SignalIcon,
  ShieldCheckIcon,
  UserIcon,
  VideoCameraIcon,
} from '@heroicons/vue/24/outline';
import WebinarMediaPickerModal from '@/Components/WebinarMediaPickerModal.vue';
import WebinarEventScheduler from '@/Components/WebinarEventScheduler.vue';
import webinarImage from '@/assets/media/images/webinar.png';

const props = defineProps({
  event: { type: Object, default: null },
  products: { type: Array, default: () => [] },
  selectedProductId: { type: [String, Number], default: '' },
  saving: { type: Boolean, default: false },
  saveError: { type: String, default: '' },
  prototypeMode: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'save']);
const router = useRouter();
const isEditing = computed(() => Boolean(props.event?.id));
const currentStep = ref(0);
const errors = reactive({ title: '', hostName: '', date: '', time: '' });
const activeMediaPicker = ref('');
const selectedIntroVideoName = ref(String(props.event?.introVideoFileName || ''));
const apiProxyUrl = String(process.env.VUE_APP_API_PROXY_URL || '').replace(/\/$/, '');

const steps = [
  { id: 'details', label: 'Event details', caption: 'Host, intro video, and thumbnail', heading: 'Tell members about the event', description: 'Add the event details and reusable Media Content members will see before the Webinar begins.' },
  { id: 'schedule', label: 'Schedule', caption: 'Date, time, and registration', heading: 'Choose when members can join', description: 'Set the event time, registration behavior, and when the waiting room becomes available.' },
  { id: 'experience', label: 'Stream setup', caption: 'Broadcast and experience', heading: 'Choose how you plan to broadcast', description: 'You can change this later before entering the studio.' },
  { id: 'review', label: 'Review', caption: 'Confirm and schedule', heading: 'Review your webinar event', description: 'Make sure the event is ready to appear for members.' },
];

const form = reactive({
  id: props.event?.id || '',
  productId: String(props.event?.productId || props.selectedProductId || props.products?.[0]?.id || ''),
  title: String(props.event?.title || ''),
  description: String(props.event?.description || ''),
  bulletPoints: Array.from({ length: 4 }, (_item, index) => String(props.event?.bulletPoints?.[index] || '')),
  hostName: String(props.event?.hostName || ''),
  hostAvatarUrl: String(props.event?.hostAvatarUrl || ''),
  hostAvatarMediaFileId: String(props.event?.hostAvatarMediaFileId || ''),
  introVideoUrl: String(props.event?.introVideoUrl || ''),
  introVideoMediaFileId: String(props.event?.introVideoMediaFileId || ''),
  image: props.event?.image || webinarImage,
  coverImageUrl: String(props.event?.coverImageUrl || ''),
  coverMediaFileId: String(props.event?.coverMediaFileId || ''),
  startMode: String(props.event?.startMode || 'scheduled'),
  date: String(props.event?.date || ''),
  time: String(props.event?.time || ''),
  timezone: String(props.event?.timezone || 'America/New_York'),
  registrationRequired: props.event?.registrationRequired !== false,
  sendReminders: props.event?.sendReminders !== false,
  waitingRoomMinutes: Number(props.event?.waitingRoomMinutes || 15),
  source: String(props.event?.source || 'browser'),
  enableChat: props.event?.enableChat !== false,
  recordReplay: props.event?.recordReplay !== false,
  enableReactions: props.event?.enableReactions !== false,
  enableModeratorAccess: false,
  moderatorDisplayName: 'Moderator',
  moderatorAvatarMediaFileId: '',
  registrations: Number(props.event?.registrations || 0),
  status: String(props.event?.status || 'Scheduled'),
});

const mediaPickerType = computed(() => (activeMediaPicker.value === 'introVideo' ? 'video' : 'image'));
const mediaPickerTitle = computed(() => ({
  hostAvatar: 'Choose host photo',
  introVideo: 'Choose Webinar intro video',
  introThumbnail: 'Choose intro video thumbnail',
  moderatorAvatar: 'Choose moderator avatar',
}[activeMediaPicker.value] || 'Choose Media Content'));
const mediaPickerOrigin = computed(() => `creator-webinar-${activeMediaPicker.value || 'media'}`);
const hostAvatarPreview = computed(() => (
  form.hostAvatarMediaFileId
    ? `${apiProxyUrl}/api/image/${encodeURIComponent(form.hostAvatarMediaFileId)}`
    : form.hostAvatarUrl
));
const introVideoThumbnailPreview = computed(() => {
  if (form.coverMediaFileId) return `${apiProxyUrl}/api/image/${encodeURIComponent(form.coverMediaFileId)}`;
  if (form.introVideoMediaFileId) return `${apiProxyUrl}/api/video-thumbnail/${encodeURIComponent(form.introVideoMediaFileId)}`;
  return form.coverImageUrl || '';
});
const moderatorAvatarPreview = computed(() => (
  form.moderatorAvatarMediaFileId
    ? `${apiProxyUrl}/api/image/${encodeURIComponent(form.moderatorAvatarMediaFileId)}`
    : ''
));
const introVideoFileName = computed(() => (
  selectedIntroVideoName.value || (form.introVideoMediaFileId ? 'Selected Media Content video' : '')
));

const selectedProductName = computed(() => (
  props.products.find((product) => String(product.id) === String(form.productId))?.name || 'Webinar'
));
const selectedProduct = computed(() => (
  props.products.find((product) => String(product.id) === String(form.productId)) || null
));
const webinarPreviewPath = computed(() => {
  const slug = String(selectedProduct.value?.slug || '').trim();
  if (!slug || selectedProduct.value?.isPrototype) return '';
  return `/creator/theme-editor/${encodeURIComponent(slug)}`;
});
const previewAvailable = computed(() => Boolean(isEditing.value && !props.prototypeMode && webinarPreviewPath.value));

const reviewDate = computed(() => {
  if (form.startMode === 'go_live_now') return 'Go live now';
  if (!form.date || !form.time) return 'Date and time not set';
  const parsed = new Date(`${form.date}T${form.time}:00`);
  if (Number.isNaN(parsed.getTime())) return `${form.date} at ${form.time}`;
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(parsed);
});

const primaryButtonLabel = computed(() => {
  if (props.saving) return 'Saving…';
  if (currentStep.value !== steps.length - 1) return 'Continue';
  return form.startMode === 'go_live_now' ? 'Save and open Studio' : 'Schedule event';
});

function validateStep(stepIndex) {
  errors.title = '';
  errors.hostName = '';
  errors.date = '';
  errors.time = '';
  if (stepIndex === 0 && !form.title) errors.title = 'Add an event title to continue.';
  if (stepIndex === 0 && !form.hostName) errors.hostName = 'Add the host name to continue.';
  if (stepIndex === 1 && form.startMode === 'scheduled' && !form.date) errors.date = 'Choose an event date.';
  if (stepIndex === 1 && form.startMode === 'scheduled' && !form.time) errors.time = 'Choose a start time.';
  return !errors.title && !errors.hostName && !errors.date && !errors.time;
}

function openMediaPicker(kind) {
  activeMediaPicker.value = kind;
}

function applySelectedMedia(media) {
  const target = activeMediaPicker.value;
  if (target === 'hostAvatar') {
    form.hostAvatarMediaFileId = String(media?.fileId || '');
    form.hostAvatarUrl = String(media?.fileUrl || '');
  } else if (target === 'introVideo') {
    form.introVideoMediaFileId = String(media?.fileId || '');
    form.introVideoUrl = String(media?.fileUrl || '');
    selectedIntroVideoName.value = String(media?.fileName || '');
  } else if (target === 'introThumbnail') {
    form.coverMediaFileId = String(media?.fileId || '');
    form.coverImageUrl = String(media?.fileUrl || '');
    form.image = String(media?.previewUrl || media?.fileUrl || webinarImage);
  } else if (target === 'moderatorAvatar') {
    form.moderatorAvatarMediaFileId = String(media?.fileId || '');
  }
  activeMediaPicker.value = '';
}

function clearHostAvatar() {
  form.hostAvatarMediaFileId = '';
  form.hostAvatarUrl = '';
}

function clearIntroVideo() {
  form.introVideoMediaFileId = '';
  form.introVideoUrl = '';
  selectedIntroVideoName.value = '';
}

function clearIntroThumbnail() {
  form.coverMediaFileId = '';
  form.coverImageUrl = '';
  form.image = webinarImage;
}

function goToStep(index) {
  if (index <= currentStep.value || index === currentStep.value + 1) {
    if (index > currentStep.value && !validateStep(currentStep.value)) return;
    currentStep.value = index;
  }
}

function handlePrimaryAction() {
  if (!validateStep(currentStep.value)) return;
  if (currentStep.value < steps.length - 1) {
    currentStep.value += 1;
    return;
  }
  saveEvent(form.startMode === 'go_live_now' ? 'Ready' : 'Scheduled');
}

function openWebinarPreview() {
  if (!previewAvailable.value) return;
  const resolved = router.resolve(webinarPreviewPath.value);
  window.open(resolved.href, '_blank', 'noopener,noreferrer');
}

function saveEvent(status) {
  emit('save', { ...form, status });
}
</script>
